import { CircleAlert, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useResubmitBlockedOrder, useRevertBlockedOrderToDraft } from '~/api';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import { OrderStatus, type OrderBlockMessage } from '~/graphql/graphql';
import { extractGraphQLErrorMessages, formatDateTime } from '~/utils';
import { getUnprocessedBlockMessages } from '~/utils/order';
import { UnprocessedBlockMessages } from '~/pages/order-view/sections/UnprocessedBlockMessages';

export function BlockedOrderCard({
  orderId,
  blockMessages,
  canReturnToDraft,
  showBuyerActions = false,
}: Readonly<{
  orderId: string;
  blockMessages: OrderBlockMessage[];
  canReturnToDraft: boolean;
  /** Buyer-only recovery actions: cancel back to draft, or retry the sync as-is. */
  showBuyerActions?: boolean;
}>) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const unprocessedMessages = getUnprocessedBlockMessages(blockMessages);
  const currentBlockMessage = unprocessedMessages.at(-1);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const { mutate: revertToDraft, isPending: isReverting } =
    useRevertBlockedOrderToDraft();
  const { mutate: resubmitOrder, isPending: isResubmitting } =
    useResubmitBlockedOrder();

  const handleRevertToDraft = () => {
    revertToDraft(
      { id: orderId },
      {
        // If the buyer already has a draft cart with this seller, the backend merges into it
        // and deletes this order — staying on /orders/view/{orderId} would 404 on refetch.
        onSuccess: (data) => {
          setShowRevertModal(false);
          if (data.revertBlockedOrderToDraft?.status === OrderStatus.Blocked) {
            toast.error(t('order.blocked_order.revert_to_draft_conflict'));
            return;
          }
          toast.success(t('order.blocked_order.revert_to_draft_success'));
          navigate('/cart');
        },
        onError: (error) => {
          toast.error(extractGraphQLErrorMessages(error).join(', '));
        },
      }
    );
  };

  const handleResubmit = () => {
    resubmitOrder(
      { id: orderId },
      {
        onSuccess: () => {
          setShowResubmitModal(false);
          toast.success(t('order.blocked_order.resubmit_success'));
        },
        onError: (error) => {
          toast.error(extractGraphQLErrorMessages(error).join(', '));
        },
      }
    );
  };

  return (
    <>
      <Modal isOpen={showRevertModal} onClose={() => setShowRevertModal(false)}>
        <div className="bg-neut-bg-alt rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-100 rounded-lg">
              <XCircle
                className="text-warning-600"
                size={24}
                aria-hidden="true"
              />
            </div>
            <h3 id="Modal-title" className="text-xl text-neut-prim font-medium">
              {t('order.blocked_order.revert_to_draft_confirm_title')}
            </h3>
          </div>
          <p className="text-neut-sec mb-4 font-light text-sm">
            {t('order.blocked_order.revert_to_draft_confirm_body')}
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowRevertModal(false)}
              disabled={isReverting}
              variant="accent"
              size="sm"
            >
              {t('order.blocked_order.revert_to_draft_confirm_keep')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevertToDraft}
              disabled={isReverting}
            >
              {t('order.blocked_order.revert_to_draft_confirm_button')}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showResubmitModal}
        onClose={() => setShowResubmitModal(false)}
      >
        <div className="bg-neut-bg-alt rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-100 rounded-lg">
              <RefreshCw
                className="text-warning-600"
                size={24}
                aria-hidden="true"
              />
            </div>
            <h3 id="Modal-title" className="text-xl text-neut-prim font-medium">
              {t('order.blocked_order.resubmit_confirm_title')}
            </h3>
          </div>
          <p className="text-neut-sec mb-4 font-light text-sm">
            {t('order.blocked_order.resubmit_confirm_body')}
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowResubmitModal(false)}
              disabled={isResubmitting}
              variant="accent"
              size="sm"
            >
              {t('order.blocked_order.resubmit_confirm_keep')}
            </Button>
            <Button onClick={handleResubmit} disabled={isResubmitting}>
              {t('order.blocked_order.resubmit_confirm_button')}
            </Button>
          </div>
        </div>
      </Modal>
      <div
        data-testid="blocked-order-card"
        className="bg-warning-50 border border-warning-200 rounded-lg p-4 flex flex-col gap-3"
      >
        <div className="flex items-start gap-3">
          <CircleAlert
            size={20}
            className="text-warning-600 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-warning-900">
              {t('order.blocked_order.title')}
            </p>
            {currentBlockMessage && (
              <p className="text-xs text-warning-600">
                {formatDateTime(currentBlockMessage.createdAt)}
              </p>
            )}
          </div>
        </div>
        {unprocessedMessages.length === 0 ? (
          <p className="text-xs text-warning-700">
            {t('order.blocked_order.no_messages')}
          </p>
        ) : (
          <>
            <p className="text-xs font-semibold text-warning-900">
              {t('order.blocked_order.errors_label')}
            </p>
            <UnprocessedBlockMessages messages={unprocessedMessages} />
            {showBuyerActions && (
              <p className="text-xs text-warning-700">
                {t(
                  canReturnToDraft
                    ? 'order.blocked_order.resolution_hint'
                    : 'order.blocked_order.resolution_hint_resubmit_only'
                )}
              </p>
            )}
          </>
        )}
        {showBuyerActions && (
          <div className="flex flex-col gap-3">
            <Button
              size="sm"
              onClick={() => setShowResubmitModal(true)}
              disabled={isReverting || isResubmitting}
            >
              <RefreshCw size={14} />
              {t('order.blocked_order.resubmit_button')}
            </Button>
            {canReturnToDraft ? (
              <Button
                variant="destructive-outline"
                size="sm"
                onClick={() => setShowRevertModal(true)}
                disabled={isReverting || isResubmitting}
              >
                <XCircle size={14} />
                {t('order.blocked_order.revert_to_draft_button')}
              </Button>
            ) : (
              <p className="text-xs text-warning-700">
                {t('order.blocked_order.revert_to_draft_in_msr')}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
